// Eine einzige Quelle für alle Navigationsdaten.
//
// Vorher stand die Liste in Layout.tsx und galt damit nur für die
// Seitenleiste. Mit der App-Ansicht lesen drei Stellen dieselben Daten —
// Seitenleiste, Tableiste und Mehr-Seite. Läge die Liste weiter in der
// Seitenleiste, könnte ein neuer Menüpunkt an zwei Stellen fehlen.
import type { Flags } from './typen';
import type { Schluessel } from './i18n';
import type { IconName } from '../komponenten/Icon';

// Bewusst nicht aus plattform.ts importiert: diese Datei läuft auch unter
// `node --test`, und dort wird ein Import ohne Dateiendung nicht aufgelöst.
// Bisher hingen hier nur Typen dran — die verschwinden beim Übersetzen.
const inHandyApp = () => {
  try { return (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};

export type NavEintrag = {
  pfad: string;
  schluessel: Schluessel;
  symbol: IconName;
  /** Flag, das der Tarif mitbringen muss — fehlt es, wird der Punkt gekennzeichnet. */
  flag?: keyof Flags;
  nurOwner?: boolean;
  nurAdmin?: boolean;
  /** Nur im Browser und in der Desktop-App — in der Handy-App ist man schon „in der App". */
  nichtInHandyApp?: boolean;
};

export type Gruppe = { titel: Schluessel; eintraege: NavEintrag[] };

export type Rolle = 'mitarbeiter' | 'admin' | 'inhaber';

export const UEBERSICHT: NavEintrag = { pfad: '/', schluessel: 'nav.uebersicht', symbol: 'uebersicht' };

// Vierzehn einzelne Punkte waren unübersichtlich. Gruppiert nach Arbeitsablauf:
// zuerst, womit täglich gearbeitet wird, Verwaltung zuletzt.
export const GRUPPEN: Gruppe[] = [
  {
    titel: 'nav.gruppe.verkauf',
    eintraege: [
      { pfad: '/kasse',      schluessel: 'nav.kasse',      symbol: 'kasse', flag: 'pos' },
      { pfad: '/belege',     schluessel: 'nav.belege',     symbol: 'belege' },
      { pfad: '/rechnungen', schluessel: 'nav.rechnungen', symbol: 'rechnungen' },
      { pfad: '/retouren',   schluessel: 'nav.retouren',   symbol: 'retouren', flag: 'returns' },
    ],
  },
  {
    titel: 'nav.gruppe.waren',
    eintraege: [
      { pfad: '/lager', schluessel: 'nav.lager', symbol: 'lager' },
    ],
  },
  {
    titel: 'nav.gruppe.kontakte',
    eintraege: [
      { pfad: '/kunden',      schluessel: 'nav.kunden',      symbol: 'kunden' },
      { pfad: '/lieferanten', schluessel: 'nav.lieferanten', symbol: 'lieferanten' },
    ],
  },
  {
    titel: 'nav.gruppe.auswertung',
    eintraege: [
      { pfad: '/buchhaltung', schluessel: 'nav.buchhaltung', symbol: 'buchhaltung', flag: 'accounting', nurAdmin: true },
      { pfad: '/bank',        schluessel: 'nav.bank',        symbol: 'bank',        flag: 'banking',    nurAdmin: true },
      { pfad: '/fahrtenbuch', schluessel: 'nav.fahrtenbuch', symbol: 'lieferanten', flag: 'accounting', nurAdmin: true },
    ],
  },
  {
    titel: 'nav.gruppe.zusammenarbeit',
    eintraege: [
      { pfad: '/assistent', schluessel: 'nav.assistent', symbol: 'assistent', flag: 'ki' },
      { pfad: '/aufgaben', schluessel: 'nav.aufgaben', symbol: 'aufgaben' },
      { pfad: '/chat',     schluessel: 'nav.chat',     symbol: 'chat' },
      { pfad: '/team',     schluessel: 'nav.team',     symbol: 'team' },
    ],
  },
  {
    titel: 'nav.gruppe.verwaltung',
    eintraege: [
      { pfad: '/firma',      schluessel: 'nav.firma',      symbol: 'firma', nurAdmin: true },
      { pfad: '/tarif',      schluessel: 'nav.tarif',      symbol: 'tarif', nurAdmin: true },
      { pfad: '/gobd',       schluessel: 'nav.gobd',       symbol: 'buchhaltung', nurAdmin: true },
      { pfad: '/uebernahme', schluessel: 'nav.uebernahme', symbol: 'uebernahme', nurAdmin: true },
      { pfad: '/einstellungen', schluessel: 'nav.einstellungen', symbol: 'monitor' },
      // LagerPro als Desktop-App: installieren oder herunterladen.
      { pfad: '/app',        schluessel: 'nav.app',        symbol: 'herunterladen', nichtInHandyApp: true },
      // Für alle, auch ohne Adminrechte: wer nicht weiterkommt, soll fragen können.
      { pfad: '/support',    schluessel: 'nav.support',    symbol: 'chat' },
      { pfad: '/owner',      schluessel: 'nav.owner',      symbol: 'mandanten', nurOwner: true },
    ],
  },
];

const ALLE: NavEintrag[] = [UEBERSICHT, ...GRUPPEN.flatMap(g => g.eintraege)];

// Die Mehr-Seite steht in keiner Gruppe — sie IST die Gruppe. Für die
// Überschrift muss sie trotzdem bekannt sein.
const MEHR: NavEintrag = { pfad: '/mehr', schluessel: 'nav.mehr', symbol: 'menue' };

/**
 * Darf diese Rolle mit diesem Tarif den Punkt überhaupt benutzen?
 *
 * Ein fehlendes Flag ist in der Seitenleiste nur eine Kennzeichnung — dort
 * sieht man, was der nächste Tarif brächte. In Tableiste und Kachelbrett
 * wäre dasselbe eine Lüge: ein Punkt, der nichts tut. Deshalb entscheidet
 * hier Flag UND Rolle gemeinsam.
 */
export function erlaubt(e: NavEintrag, rolle: Rolle, flags: Flags | null): boolean {
  if (e.nurOwner && rolle !== 'inhaber') return false;
  if (e.nichtInHandyApp && inHandyApp()) return false;
  // Der Inhaber ist in seinem eigenen Betrieb auch Administrator.
  if (e.nurAdmin && rolle === 'mitarbeiter') return false;
  if (e.flag && !flags?.[e.flag]) return false;
  return true;
}

// Wunschreihenfolge je Rolle. Die ersten vier erlaubten Punkte gewinnen —
// fällt einer wegen Tarif oder Rolle aus, rückt der nächste nach, damit die
// Leiste nie eine Lücke zeigt.
const WUNSCH: Record<Rolle, string[]> = {
  mitarbeiter: ['/', '/lager', '/kasse', '/aufgaben', '/belege', '/rechnungen', '/chat'],
  admin:       ['/', '/rechnungen', '/lager', '/aufgaben', '/kasse', '/buchhaltung', '/kunden'],
  inhaber:     ['/', '/owner', '/support', '/aufgaben', '/rechnungen', '/lager'],
};

/** Die vier Punkte der Tableiste. Immer genau vier, solange es vier erlaubte gibt. */
export function tableiste(rolle: Rolle, flags: Flags | null): NavEintrag[] {
  const gewaehlt: NavEintrag[] = [];
  for (const pfad of WUNSCH[rolle]) {
    if (gewaehlt.length === 4) break;
    const e = ALLE.find(x => x.pfad === pfad);
    if (e && erlaubt(e, rolle, flags)) gewaehlt.push(e);
  }
  return gewaehlt;
}

/**
 * Alles, was nicht in der Leiste steht — gruppiert wie in der Seitenleiste.
 * Leere Gruppen fallen weg: eine Überschrift ohne Einträge ist nur Lärm.
 */
export function mehrGruppen(rolle: Rolle, flags: Flags | null, inLeiste: string[]): Gruppe[] {
  return GRUPPEN
    .map(g => ({
      titel: g.titel,
      eintraege: g.eintraege.filter(e => erlaubt(e, rolle, flags) && !inLeiste.includes(e.pfad)),
    }))
    .filter(g => g.eintraege.length > 0);
}

/**
 * Überschrift für einen Pfad — der längste passende Eintrag gewinnt.
 *
 * Dadurch erbt `/kunden/kd_1` die Überschrift von `/kunden`, ohne dass eine
 * Seite ihren Titel selbst mitgeben muss. Und `/` erbt nicht an alles weiter,
 * obwohl es Präfix jedes Pfades ist.
 */
export function titelSchluessel(pfad: string): Schluessel | null {
  let treffer: NavEintrag | null = null;
  for (const e of [...ALLE, MEHR]) {
    if (e.pfad === '/' ? pfad === '/' : pfad === e.pfad || pfad.startsWith(e.pfad + '/')) {
      if (!treffer || e.pfad.length > treffer.pfad.length) treffer = e;
    }
  }
  return treffer ? treffer.schluessel : null;
}
